import React, { useState, useRef, useCallback } from 'react';
import { Editor } from 'primereact/editor';

export default function ReplyComposer({ onSubmit, submitting }) {
  const [mode, setMode] = useState('reply'); // 'reply' | 'internal_note'
  const [html, setHtml] = useState('');
  const [editorHeight, setEditorHeight] = useState(120);
  const editorRef = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = editorHeight;

    const onMouseMove = (ev) => {
      if (!dragging.current) return;
      // Dragging up = larger editor (startY - clientY is positive when moving up)
      const delta = startY.current - ev.clientY;
      setEditorHeight(Math.max(60, Math.min(500, startH.current + delta)));
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [editorHeight]);

  const handleSubmit = (status) => {
    if (!html || !html.replace(/<[^>]*>/g, '').trim()) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const bodyText = tempDiv.textContent || tempDiv.innerText || '';

    onSubmit({
      type: mode,
      body_html: html,
      body_text: bodyText,
      status,
    });
    setHtml('');
  };

  const isNote = mode === 'internal_note';

  return (
    <div className="reply-composer">
      {/* Drag handle */}
      <div className="composer-resize-handle" onMouseDown={onMouseDown}>
        <div className="composer-resize-grip" />
      </div>
      <div className="composer-tabs">
        <button
          className={`composer-tab ${mode === 'reply' ? 'active' : ''}`}
          onClick={() => setMode('reply')}
        >
          Public Reply
        </button>
        <button
          className={`composer-tab ${isNote ? 'note-active' : ''}`}
          onClick={() => setMode('internal_note')}
        >
          Internal Note
        </button>
      </div>
      <div className="composer-body" style={isNote ? { background: '#fefce8' } : {}}>
        <Editor
          ref={editorRef}
          value={html}
          onTextChange={(e) => setHtml(e.htmlValue || '')}
          style={{ height: editorHeight }}
          placeholder={isNote ? 'Add an internal note...' : 'Type your reply...'}
        />
      </div>
      <ActionBar
        onSubmit={handleSubmit}
        submitting={submitting}
        isNote={isNote}
      />
    </div>
  );
}

function ActionBar({ onSubmit, submitting, isNote }) {
  const [stayOnTicket, setStayOnTicket] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('pending');

  const statuses = [
    { value: 'open', label: 'Open' },
    { value: 'pending', label: 'Pending' },
    { value: 'solved', label: 'Solved' },
  ];

  const selected = statuses.find((s) => s.value === selectedStatus) || statuses[1];

  return (
    <div className="action-bar">
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--topcx-text-muted)', cursor: 'pointer', marginRight: 'auto' }}>
        <input
          type="checkbox"
          checked={stayOnTicket}
          onChange={(e) => setStayOnTicket(e.target.checked)}
          style={{ accentColor: 'var(--topcx-primary)' }}
        />
        Stay on ticket
      </label>

      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onSubmit(selectedStatus)}
          disabled={submitting}
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, paddingRight: 12 }}
        >
          {submitting ? 'Submitting...' : `Submit as ${selected.label}`}
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={submitting}
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid rgba(255,255,255,0.3)',
            paddingLeft: 6,
            paddingRight: 6,
          }}
        >
          &#9662;
        </button>
        {showDropdown && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: 4,
              background: '#fff',
              border: '1px solid var(--topcx-border)',
              borderRadius: 8,
              boxShadow: 'var(--topcx-shadow-md)',
              minWidth: 160,
              zIndex: 10,
              overflow: 'hidden',
            }}
          >
            {statuses.map((s) => (
              <button
                key={s.value}
                onClick={() => { setSelectedStatus(s.value); setShowDropdown(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: s.value === selectedStatus ? 'rgba(99,102,241,0.08)' : '#fff',
                  textAlign: 'left',
                  fontSize: '0.8rem',
                  fontWeight: s.value === selectedStatus ? 600 : 400,
                  cursor: 'pointer',
                  color: 'var(--topcx-text)',
                }}
              >
                Submit as {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { ActionBar };
